import React, { useState, useEffect } from 'react';
import AppModal from './Modal/AppModal';

export interface Expense {
  _id: string;
  name: string;
  price: number;
  isRecurring: boolean;
  isPaid: boolean;
  category?: string;
}

interface SelectiveResetModalProps {
  open: boolean;
  onClose: () => void;
  expenses: Expense[];
  onConfirm: (expenseIds: string[]) => void;
  t: any;
}

const SelectiveResetModal: React.FC<SelectiveResetModalProps> = ({
  open,
  onClose,
  expenses,
  onConfirm,
  t,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (expenses.length > 0) {
      setSelectedIds(new Set(expenses.map((e) => e._id)));
    }
  }, [expenses]);

  const toggleExpense = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const handleConfirm = () => { onConfirm(Array.from(selectedIds)); onClose(); };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={t.dashboardPage?.selectExpensesToDelete || 'Select One-Time Expenses to Delete'}
      actions={{
        secondary: { label: t.cancel || 'Cancel', onClick: onClose },
        primary: {
          label: `${t.delete || 'Delete'} (${selectedIds.size})`,
          onClick: handleConfirm,
          disabled: selectedIds.size === 0,
        },
      }}
    >
      <div className="py-2 space-y-4">
        {/* Warning banner */}
        <div className="flex items-start gap-2.5 p-3 rounded-xl bg-orange/10 border border-orange/20 text-sm text-[var(--text-color)]">
          <span className="text-orange mt-0.5">⚠</span>
          <span>
            {t.dashboardPage?.selectExpensesDeleteHelp ||
              'Recurring expenses have been reset. These one-time expenses are selected for deletion. Uncheck any you want to KEEP.'}
          </span>
        </div>

        {expenses.length > 0 && (
          <div>
            <p className="text-base font-semibold text-[var(--text-color)] mb-2 flex items-center gap-2">
              {t.dashboardPage?.oneTimeExpenses || 'One-Time Expenses'} ({expenses.length})
            </p>
            <div className="flex flex-col gap-2">
              {expenses.map((expense) => {
                const checked = selectedIds.has(expense._id);
                return (
                  <label
                    key={expense._id}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      checked
                        ? 'border-primary/30 bg-primary/8'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleExpense(expense._id)}
                      className="w-4 h-4 accent-primary flex-shrink-0"
                    />
                    <span className="flex-1 text-sm text-[var(--text-color)]">{expense.name}</span>
                    <span className="text-sm font-semibold text-primary">${expense.price?.toFixed(2)}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {expenses.length === 0 && (
          <div className="flex items-start gap-2.5 p-3 rounded-xl bg-primary/10 border border-primary/20 text-sm text-[var(--text-color)]">
            <span className="text-primary mt-0.5">ℹ</span>
            <span>
              {t.dashboardPage?.noOneTimeExpenses || 'No one-time expenses to delete. All expenses are recurring.'}
            </span>
          </div>
        )}
      </div>
    </AppModal>
  );
};

export default SelectiveResetModal;
